import { logout } from '../../func.ts';
import { useEffect, useMemo, useRef, useState } from 'react';
import Axios from 'axios';
import moment from 'moment/moment';
import M from 'materialize-css';
import { BsUpcScan } from 'react-icons/bs';
import { HiOutlineDocumentArrowDown } from 'react-icons/hi2';
import MimeTypes from '../../utilities/mimeTypes';
import { Link } from 'react-router-dom';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import useUrlStorage from '../../hooks/useUrlStorage.ts';
import Paginator from '../../components/layout/paginator.tsx';
import queryHooks from '../../common/queryHooks/index.ts';
import { useDebounceValue } from '../../common/useDebounce.ts';
import api from '../../common/api/index.ts';
import useCurrentCustomerLocationId from '../../hooks/useCurrentCustomerLocation.ts';
const { NODE_ENV } = process.env;

const OrderOverview = () => {
  const { customerLocationId } = useCurrentCustomerLocationId();

  const initSearchParams = () => ({
    customerLocationId: Number(customerLocationId) || 0,
    senderName: '',
    purchaseOrderNumber: '',
    docDateStart: moment().startOf('week').format('YYYY-MM-DD'),
    docDateEnd: moment().format('YYYY-MM-DD'),
  });

  const ref = useRef({
    abortController: new AbortController(),
  });

  const pageSize = useMemo(() => 15, []);

  const [searchParams, setSearchParams] = useUrlStorage(
    'searchParams',
    initSearchParams()
  );
  const [downloadInProgressIds, setDownloadInProgressIds] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);

  const getAuthData = () => JSON.parse(localStorage.getItem('auth_data'));

  const debouncedSearchParams = useDebounceValue(searchParams, 500);

  const {
    data: orderList,
    isLoading,
    refetch: search,
  } = queryHooks.order.useReadManySearch(
    {
      filters: debouncedSearchParams,
      paginationOptions: { pageSize, pageNumber },
    },
    {
      enabled: Boolean(debouncedSearchParams.customerLocationId),
      queryFn: ({ queryKey }) => {
        const params = queryKey[2];
        const {
          senderName,
          purchaseOrderNumber,
          docDateStart,
          docDateEnd,
          customerLocationId,
        } = params.filters;

        if (!customerLocationId) return null;
        const time = moment().format(' HH:mm:ss');

        return api.order.read.many.search({
          ...params,
          filters: {
            senderName: senderName ? senderName.trim() : '',
            purchaseOrderNumber: purchaseOrderNumber
              ? purchaseOrderNumber.trim()
              : '',
            docDateStart: docDateStart + ' 00:00:00',
            docDateEnd: docDateEnd + time,
            customerLocationId: customerLocationId ?? 0,
          },
        });
      },
      select: ({ data, total }) => {
        const orderList = [];
        data.forEach(row => {
          const { purchaseOrderNumbers } = row;
          const pos = purchaseOrderNumbers
            ?.filter((po, index, self) => self.indexOf(po) === index)
            .map(po => po?.split(':').pop());

          orderList.push({
            ...row,
            purchaseOrderNumbers: pos,
          });
        });

        return { data: orderList, total };
      },
    }
  );

  useEffect(() => {
    return NODE_ENV === 'production'
      ? () => ref.current?.abortController.abort()
      : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = ({ target: { id, value } }) =>
    setSearchParams(p => ({ ...p, [id]: value }));

  const download = documentId => {
    if (downloadInProgressIds.includes(documentId)) return;

    ref.current.abortController = new AbortController();
    setDownloadInProgressIds(p => [...p, documentId]);

    Axios.get('/api/v1/file/download/epcisdoc', {
      params: {
        ...getAuthData(),
        documentId,
      },
      signal: ref.current.abortController?.signal,
    })
      .then(result => {
        const { document: doc, buffer } = result.data;

        const uint8Array = Uint8Array.from(buffer.data);
        const blob = new Blob([uint8Array]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.hidden = true;
        link.href = url;
        link.setAttribute(
          'download',
          `epcis_doc_from_${doc.name}_${moment(doc.timeStamp).format(
            'YYMMDD'
          )}.${MimeTypes.getExtensions(doc.contentType)?.[0] || 'xml'}`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(logout)
      .finally(() =>
        setDownloadInProgressIds(p => p.filter(id => id !== documentId))
      );
  };

  const enterListener = ({ key }) => {
    if (key?.toLowerCase() === 'enter') search();
  };

  useEffect(() => {
    M.updateTextFields();
  }, [searchParams]);

  useEffect(() => {
    const id = Number(customerLocationId);

    setSearchParams(prev =>
      prev.customerLocationId === id
        ? prev
        : { ...prev, customerLocationId: id }
    );
  }, [customerLocationId, setSearchParams]);

  return (
    <div>
      <div className="row">
        <div className="input-field col s12 m4">
          <input
            id="senderName"
            type="text"
            onChange={onChange}
            onKeyDown={enterListener}
            value={searchParams.senderName}
          />
          <label htmlFor="senderName">Sender Name</label>
        </div>
        <div className="input-field col s12 m2">
          <input
            id="purchaseOrderNumber"
            type="text"
            onChange={onChange}
            onKeyDown={enterListener}
            value={searchParams.purchaseOrderNumber}
          />
          <label htmlFor="purchaseOrderNumber">PO Number</label>
        </div>

        <div className="input-field col s12 m2">
          <input
            id="docDateStart"
            type="date"
            onChange={onChange}
            onKeyDown={enterListener}
            value={searchParams.docDateStart}
          />
          <label htmlFor="docDateStart">Date Start</label>
        </div>
        <div className="input-field col s12 m2">
          <input
            id="docDateEnd"
            type="date"
            onChange={onChange}
            onKeyDown={enterListener}
            value={searchParams.docDateEnd}
          />
          <label htmlFor="docDateEnd">Date End</label>
        </div>
        <div className="input-field col s12 m2">
          <a
            style={{ position: 'relative', top: '8px' }}
            href="/"
            className="btn-small blue white-text waves-effect waves-light col s12"
            onClick={e => {
              e.preventDefault();
              search();
            }}
            disabled={isLoading ? 'disabled' : ''}
          >
            {isLoading ? 'Searching...' : 'Search Orders'}
          </a>
        </div>
      </div>
      <div className="row">
        {orderList?.data?.length ? (
          <>
            <table className="highlight">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Recipient GLN</th>
                  <th>Sender</th>
                  <th>PO #</th>
                  <th>Date Received</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orderList?.data?.map(o => (
                  <tr key={o.documentId}>
                    <td style={{ padding: '2px' }}>{o.customerLocationName}</td>
                    <td style={{ padding: '2px' }}>{o.globalLocationNumber}</td>
                    <td style={{ padding: '2px' }}>
                      {o.senderNames?.join(', ')}
                    </td>
                    <td style={{ padding: '2px' }}>
                      {o.purchaseOrderNumbers?.join(', ')}
                    </td>
                    <td style={{ padding: '2px' }}>
                      {moment(o.timeStamp).format('MM/DD/YY HH:mm')}
                    </td>
                    <td
                      style={{
                        padding: '2px',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-around',
                      }}
                    >
                      <Link
                        to={`/orders/receive/${o.documentId}`}
                        className="black-text"
                      >
                        <BsUpcScan
                          style={{
                            fontSize: '1.3em',
                            position: 'relative',
                            top: '3px',
                            cursor: 'pointer',
                          }}
                        />
                      </Link>
                      {downloadInProgressIds?.includes(o.documentId) ? (
                        <div className="preloader-wrapper tiny active">
                          <div className="spinner-layer spinner-blue-only">
                            <div className="circle-clipper left">
                              <div className="circle"></div>
                            </div>
                            <div className="gap-patch">
                              <div className="circle"></div>
                            </div>
                            <div className="circle-clipper right">
                              <div className="circle"></div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <HiOutlineDocumentArrowDown
                          style={{
                            fontSize: '1.3em',
                            position: 'relative',
                            top: '3px',
                            cursor: 'pointer',
                          }}
                          onClick={() => download(o.documentId)}
                        />
                      )}
                      {parseInt(o.amountNotReceived) === 0 ? (
                        <IoMdCheckmarkCircleOutline
                          className="green-text"
                          style={{
                            fontSize: '1.4em',
                            position: 'relative',
                            top: '3px',
                          }}
                        />
                      ) : (
                        <div style={{ width: '21px' }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginator
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              totalItems={orderList?.total ?? 0}
            />
          </>
        ) : (
          <h6
            className="orange-text"
            style={{ position: 'relative', left: '80px' }}
          >
            <i>
              No results. Please modify the search parameters and search again.
            </i>
          </h6>
        )}
      </div>
    </div>
  );
};

export default OrderOverview;
